import { BehaviorSubject } from "rxjs";
import { ModelStyle } from "@iventis/domain-model/model/modelStyle";
import { LineModelStyle } from "@iventis/domain-model/model/lineModelStyle";
import { EMPTY_GUID } from "@iventis/utilities";
import { getStaticStyleValue } from "@iventis/layer-style-helpers";
import { changeModelColour } from "@iventis/model-utilities/src/change-model-colour";
import { addImageToModel } from "@iventis/model-utilities/src/add-image-to-model";
import type { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { AreaModelStyle } from "@iventis/domain-model/model/areaModelStyle";
import type { MapModuleLayer, MapState } from "../../types/store-schema";
import { Layer } from "./layer";
import { getCustomModelLayerStyle, getModelLayerStyle } from "../../utilities/layer.helpers";
import { getStaticAndMappedValues } from "../../utilities/style-helpers";
import type { ModelDataStore, ModelData } from "../../data-store/model-data-store";

export abstract class ModelLayer<TSource, TStyle extends ModelStyle | LineModelStyle | AreaModelStyle> extends Layer<TSource, TStyle> {
    public readonly layerId: string;

    public readonly sourceId: string;

    protected modelData: ModelData[] = [];

    protected dataFieldListItems: Record<string, DataFieldListItem[]> = {};

    private readonly modelDataStore: ModelDataStore;

    constructor(layer: MapModuleLayer, modelDataStore: ModelDataStore, state$: BehaviorSubject<MapState>) {
        super(state$);
        this.layerId = layer.id;
        this.sourceId = layer.id;
        this.modelDataStore = modelDataStore;
    }

    /** Loads all the models needed for a layer */
    protected async loadModel(iventisLayer: MapModuleLayer) {
        this.modelData = [];
        const { model: modelStyleValue } = getModelLayerStyle(iventisLayer);
        const customisableModelStyle = getCustomModelLayerStyle(iventisLayer);
        const modelIds = getStaticAndMappedValues(modelStyleValue);
        const imageId = getStaticStyleValue(customisableModelStyle?.customImage);
        const isCustomColourBeingUsed = getStaticStyleValue(customisableModelStyle?.customColour);
        const useCustomImage = imageId != null && imageId !== EMPTY_GUID;
        const loadedModels = await this.modelDataStore.getModelById(modelIds);

        for (const loadedModel of loadedModels) {
            let model = { ...loadedModel };

            // Model has custom image
            if (useCustomImage && loadedModel.customImage) {
                // Get image asset url
                const imageUrl = await this.modelDataStore.getModelImageAssetUrl(imageId);
                model = {
                    ...model,
                    modelId: this.createModelId(model.baseModelId, imageId, undefined, model),
                    // Add image to model
                    modelFileBlob: await addImageToModel(model.modelFileBlob, imageUrl),
                };
            }

            // Model has custom colours
            if (isCustomColourBeingUsed && loadedModel.customColour) {
                const colourValues = getStaticAndMappedValues(customisableModelStyle?.colour);
                // Each custom colour requires a different model to be used
                for (const colourValue of colourValues) {
                    const modelWithColour = {
                        ...model,
                        modelId: this.createModelId(model.baseModelId, imageId, colourValue, model),
                        modelFileBlob: await changeModelColour(model.modelFileBlob, colourValue),
                    };
                    this.modelData.push(modelWithColour);
                }
            } else {
                this.modelData.push(model);
            }
        }
    }

    /**
     * Generates a model id depending on if it is using a custom image or custom colour
     * As we need to load a different model for when a custom image or custom image is being used we need to generate unique ids for each customised model
     *
     * @example
     * Model - "{modelId}"
     * Model with image - "{modelId}:{customImageId}"
     * Model with colour - "{modelId}:{colourValue}"
     * Model with image and colour - "{modelId}:{customImageId}:{colourValue}"
     */
    public createModelId(modelId: string, customImageId?: string, colourValue?: string, _model?: ModelData) {
        let id = modelId;
        const model = _model ?? this.modelData.find(({ baseModelId }) => baseModelId === modelId);

        // If custom image id is EMPTY_GUID we know that it does not have a custom image
        if (customImageId != null && customImageId !== EMPTY_GUID && model.customImage) {
            id += `_${customImageId}`;
        }

        if (colourValue != null && model.customColour) {
            id += `_${colourValue}`;
        }

        return id;
    }
}
